import { useEffect, useState } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const SECTIONS = [
  {
    title: '💳 Abonnements prestataires',
    icon: '💳',
    fields: [
      { key: 'subscription_price_monthly', label: 'Prix mensuel (FCFA)', type: 'number', min: 0, hint: 'Montant payé par les prestataires pour publier leurs offres' },
      { key: 'subscription_duration_days', label: 'Durée (jours)', type: 'number', min: 1, hint: 'Durée de validité d\'un abonnement mensuel' },
    ],
  },
  {
    title: '💰 Commission plateforme',
    icon: '💰',
    fields: [
      { key: 'commission_rate', label: 'Taux de commission (%)', type: 'number', min: 0, max: 100, hint: 'Pourcentage prélevé sur le prix final de la prestation' },
    ],
  },
];

export default function Settings() {
  const toast = useToast();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/admin/settings')
      .then(r => setSettings(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast('Paramètres enregistrés avec succès', 'success');
      setDirty(false);
    } catch {
      toast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div>
      <div className="page-header">
        <div className="skeleton" style={{ height: 28, width: 250, marginBottom: 8 }} />
      </div>
      {[1, 2].map(i => <div key={i} className="skeleton" style={{ height: 160, borderRadius: 12, marginBottom: 16 }} />)}
    </div>
  );

  return (
    <div style={{ maxWidth: 680 }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 className="page-title">Configuration</h2>
          <p className="page-subtitle">Paramètres économiques de la plateforme</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? <><div className="spinner" style={{ width: 14, height: 14, borderTopColor: '#fff' }} /> Sauvegarde...</> : '💾 Enregistrer'}
        </button>
      </div>

      {SECTIONS.map(section => (
        <div key={section.title} className="card animate-fade" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <span className="card-title">{section.title}</span>
          </div>
          <div className="card-body">
            {section.fields.map(field => (
              <div key={field.key} className="form-group">
                <label className="form-label">{field.label}</label>
                <input
                  className="form-input"
                  type={field.type}
                  min={field.min}
                  max={field.max}
                  value={settings[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={{ maxWidth: 280 }}
                />
                {field.hint && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>{field.hint}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Preview card */}
      <div className="card animate-fade" style={{ animationDelay: '0.15s', background: 'var(--primary-light)', border: '1px solid rgba(233,69,96,0.2)' }}>
        <div className="card-body">
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 12 }}>📋 Aperçu du modèle économique</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Abonnement prestataire', value: `${settings.subscription_price_monthly || 0} FCFA / ${settings.subscription_duration_days || 30} jours` },
              { label: 'Commission mise en relation', value: `${settings.commission_rate || 10}% du prix final` },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--surface)', borderRadius: 8, padding: '12px 16px' }}>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{item.label}</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
